import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useAuth,
} from '@aaas/shared';
import campaignService from '../services/campaign.service';
import type { Campaign, CampaignCreateInput, CampaignUpdateInput, CampaignStatus } from '../types';

interface CampaignFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  campaign?: Campaign | null;
  onSuccess: () => void;
}

export function CampaignFormDialog({
  open,
  onClose,
  mode,
  campaign,
  onSuccess,
}: CampaignFormDialogProps) {
  const { t } = useTranslation('ats');
  const { user, currentWorkspace } = useAuth();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<CampaignStatus>('active');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens or campaign changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && campaign) {
        setCode(campaign.code);
        setName(campaign.name);
        setDescription(campaign.description || '');
        setStatus(campaign.status);
      } else {
        setCode('');
        setName('');
        setDescription('');
        setStatus('active');
      }
      setError(null);
    }
  }, [open, mode, campaign]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentWorkspace) return;
    if (!code.trim()) {
      setError(t('common.validation.codeRequired'));
      return;
    }
    if (!name.trim()) {
      setError(t('common.validation.nameRequired'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      if (mode === 'create') {
        const data: CampaignCreateInput = {
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || undefined,
          status,
        };
        await campaignService.createCampaign(currentWorkspace.id, token, data);
      } else if (campaign) {
        const data: CampaignUpdateInput = {
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || undefined,
          status,
        };
        await campaignService.updateCampaign(campaign.id, currentWorkspace.id, token, data);
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} campaign:`, err);
      const errorMessage = err instanceof Error ? err.message : t('config.unknownError');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? t('campaign.createTitle') : t('campaign.editTitle')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="code">{t('campaign.codeLabel')}</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t('campaign.codePlaceholder')}
                disabled={isSubmitting}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">{t('campaign.statusLabel')}</Label>
              <Select value={status} onValueChange={(value) => setStatus(value as CampaignStatus)}>
                <SelectTrigger className={isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}>
                  <SelectValue placeholder={t('campaign.statusPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t('campaign.statusActive')}</SelectItem>
                  <SelectItem value="inactive">{t('campaign.statusInactive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">{t('campaign.nameLabel')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('campaign.namePlaceholder')}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('campaign.descriptionLabel')}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('campaign.descriptionPlaceholder')}
              disabled={isSubmitting}
              rows={3}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('config.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {mode === 'create' ? t('common.creating') : t('common.saving')}
                </>
              ) : mode === 'create' ? (
                t('campaign.createButton')
              ) : (
                t('common.saveChanges')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
